import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ClipboardList, Siren } from 'lucide-react';
import { useTranslation } from '../i18n/useTranslation';
import { AppLayout } from '../components/templates/AppLayout/AppLayout';
import { Header } from '../components/layout/Header';
import { Sidebar } from '../components/Sidebar';
import { PageContent } from '../components/templates/PageContent/PageContent';
import { PageHeader } from '../components/templates/PageHeader/PageHeader';
import { Section } from '../components/templates/Section/Section';
import { Alert } from '../components/atoms/Alert/Alert';
import { Badge } from '../components/atoms/Badge/Badge';
import { Button } from '../components/atoms/Button/Button';
import { Card } from '../components/atoms/Card/Card';
import { EmptyState } from '../components/atoms/EmptyState';
import { Inline } from '../components/atoms/Inline/Inline';
import { LiveRegion } from '../components/atoms/LiveRegion/LiveRegion';
import { Stack } from '../components/atoms/Stack/Stack';
import { Heading } from '../components/atoms/Typography/Heading';
import { Text } from '../components/atoms/Typography/Text';
import { LoadErrorState } from '../components/feedback/LoadErrorState/LoadErrorState';
import { ReportIncidentDialog } from '../components/incidents/ReportIncidentDialog';
import { InterventionDialog } from '../components/incidents/InterventionDialog';
import { formatDay, incidentTitle, localDay } from '../components/incidents/incidentText';
import {
  deleteIntervention,
  incidentsKey,
  interventionsKey,
  listIncidents,
  listInterventions,
} from '../services/incidentService';
import './IncidentsPage.css';

/**
 * "Vorfälle & Eingriffe" (Reef Guard #1824): incidents — opened automatically
 * by a livestock death or a critical Reef Guard finding, or by hand — and the
 * intervention log ("I changed X"), whose entries also mark the measurement charts.
 */
export function IncidentsPage() {
  const { t, i18n } = useTranslation();
  const language = i18n.language || 'de';
  const { aquariumId = '' } = useParams<{ aquariumId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [reportOpen, setReportOpen] = useState(false);
  const [interventionOpen, setInterventionOpen] = useState(false);
  const [announcement, setAnnouncement] = useState('');

  const incidents = useQuery({ queryKey: incidentsKey(aquariumId), queryFn: () => listIncidents(aquariumId), enabled: Boolean(aquariumId) });
  const interventions = useQuery({ queryKey: interventionsKey(aquariumId), queryFn: () => listInterventions(aquariumId), enabled: Boolean(aquariumId) });
  const removeIntervention = useMutation({
    mutationFn: (id: string) => deleteIntervention(aquariumId, id),
    onSuccess: () => {
      setAnnouncement(t('incidents.intervention.deleted'));
      void queryClient.invalidateQueries({ queryKey: interventionsKey(aquariumId) });
    },
  });

  let incidentBody;
  if (incidents.isPending) {
    incidentBody = <LiveRegion busy><Text color="muted">{t('incidents.loading')}</Text></LiveRegion>;
  } else if (incidents.isError) {
    incidentBody = (
      <LoadErrorState title={t('incidents.loadError')} onRetry={() => void incidents.refetch()} retryLabel={t('incidents.retry')} titleLevel={3} />
    );
  } else if (incidents.data.length === 0) {
    incidentBody = (
      <EmptyState
        icon={<Siren aria-hidden="true" />}
        headline={t('incidents.empty.title')}
        description={t('incidents.empty.description')}
        actionLabel={t('incidents.report.open')}
        onAction={() => setReportOpen(true)}
        headingLevel={3}
      />
    );
  } else {
    incidentBody = (
      <ul className="incidents-page__list" data-testid="incident-list">
        {incidents.data.map((incident) => (
          <li key={incident.id}>
            <Card>
              <Stack gap="2">
                <Heading level={3}>
                  <Link className="incidents-page__link" to={`/aquariums/${aquariumId}/incidents/${incident.id}`}>
                    {incidentTitle(t, incident)}
                  </Link>
                </Heading>
                <Inline gap="2" wrap>
                  <Text color="secondary">{formatDay(localDay(incident.anchorAt), language)}</Text>
                  <Badge tone={incident.closed ? 'neutral' : 'warning'}>{t(incident.closed ? 'incidents.status.closed' : 'incidents.status.open')}</Badge>
                </Inline>
                {incident.note && <Text>{incident.note}</Text>}
              </Stack>
            </Card>
          </li>
        ))}
      </ul>
    );
  }

  let interventionBody;
  if (interventions.isPending) {
    interventionBody = <Text color="muted">{t('incidents.loading')}</Text>;
  } else if (interventions.isError) {
    interventionBody = (
      <LoadErrorState
        title={t('incidents.intervention.loadError')}
        onRetry={() => void interventions.refetch()}
        retryLabel={t('incidents.retry')}
        titleLevel={3}
      />
    );
  } else if (interventions.data.length === 0) {
    interventionBody = (
      <EmptyState
        icon={<ClipboardList aria-hidden="true" />}
        headline={t('incidents.intervention.emptyTitle')}
        description={t('incidents.intervention.emptyDescription')}
        actionLabel={t('incidents.intervention.open')}
        onAction={() => setInterventionOpen(true)}
        headingLevel={3}
      />
    );
  } else {
    interventionBody = (
      <ul className="incidents-page__list" data-testid="intervention-list">
        {interventions.data.map((i) => (
          <li key={i.id}>
            <Card>
              <Inline gap="3" align="center" wrap className="incidents-page__intervention">
                <Stack gap="1" className="incidents-page__intervention-text">
                  <Text weight="semibold">{t(`incidents.interventionKinds.${i.kind}`)}</Text>
                  <Text color="secondary" size="sm">
                    {i.endedAt
                      ? t('incidents.intervention.period', { from: formatDay(localDay(i.occurredAt), language), to: formatDay(localDay(i.endedAt), language) })
                      : formatDay(localDay(i.occurredAt), language)}
                  </Text>
                  {i.note && <Text>{i.note}</Text>}
                </Stack>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => removeIntervention.mutate(i.id)}
                  disabled={removeIntervention.isPending}
                  aria-label={t('incidents.intervention.deleteLabel', { kind: t(`incidents.interventionKinds.${i.kind}`) })}
                >
                  {t('incidents.intervention.delete')}
                </Button>
              </Inline>
            </Card>
          </li>
        ))}
      </ul>
    );
  }

  return (
    <AppLayout
      header={<Header />}
      sidebar={<Sidebar />}
      main={
        <PageContent width="default">
          <PageHeader
            title={t('incidents.title')}
            description={t('incidents.subtitle')}
            actions={<Button onClick={() => setReportOpen(true)}>{t('incidents.report.open')}</Button>}
          />
          <LiveRegion visuallyHidden>{announcement}</LiveRegion>
          <Section title={t('incidents.section.incidents')} description={t('incidents.section.incidentsDescription')}>
            {incidentBody}
          </Section>
          <Section
            title={t('incidents.section.interventions')}
            description={t('incidents.section.interventionsDescription')}
            actions={<Button variant="secondary" onClick={() => setInterventionOpen(true)}>{t('incidents.intervention.open')}</Button>}
          >
            <Stack gap="4">
              {removeIntervention.isError && <Alert>{t('incidents.intervention.deleteError')}</Alert>}
              {interventionBody}
            </Stack>
          </Section>
          <ReportIncidentDialog
            aquariumId={aquariumId}
            isOpen={reportOpen}
            onClose={() => setReportOpen(false)}
            onCreated={(incident) => {
              setReportOpen(false);
              void queryClient.invalidateQueries({ queryKey: incidentsKey(aquariumId) });
              navigate(`/aquariums/${aquariumId}/incidents/${incident.id}`);
            }}
          />
          <InterventionDialog
            aquariumId={aquariumId}
            isOpen={interventionOpen}
            onClose={() => setInterventionOpen(false)}
            onCreated={() => {
              setInterventionOpen(false);
              setAnnouncement(t('incidents.intervention.saved'));
              void queryClient.invalidateQueries({ queryKey: interventionsKey(aquariumId) });
            }}
          />
        </PageContent>
      }
    />
  );
}

export default IncidentsPage;
